/**
 * ContainerGroupHeader — project/list header shown in grouped views
 * (Today, Anyday, Someday).
 *
 * Layout:
 *   [TimelineIcon]  [AreaName >>] ContainerName  [count badge]
 *   ────────────────── border-bottom ──────────────────────────
 *
 * Area-prefix is shown when the container belongs to an area.
 * Clicking the area name navigates to the area page.
 * Clicking the container name navigates to the project/list page.
 */

import { memo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { hasVisibleTitle, getTitleWithFallback } from "@/shared/utils/general/titleUtils"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { StaticIcon } from "@/shared/icons/StaticIcon"
import { ObjectType } from "@/db/schema"
import { useAncestorTagStore } from "@/features/tags/ancestorTagStore"
import type { ContainerProgress } from "@/features/tags/ancestorTagStore"
import ChevronBigRight from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-big-right.svg?react"

interface ContainerGroupHeaderProps {
  containerId: string
  title: string
  type: ObjectType
  progress: ContainerProgress
  /** Number of tasks in this group (for the badge) */
  taskCount: number
}

export const ContainerGroupHeader = memo(function ContainerGroupHeader({
  containerId,
  title,
  type,
  progress,
  taskCount: _taskCount,
}: ContainerGroupHeaderProps) {
  const { t } = useTranslation()
  const isArea = type === ObjectType.Area
  const isProject = type === ObjectType.Project
  const href = isArea
    ? `/area/${containerId}`
    : isProject
      ? `/project/${containerId}`
      : `/list/${containerId}`
  const hasTitle = hasVisibleTitle(title)
  const displayTitle = hasTitle
    ? title
    : getTitleWithFallback({ title, t, objectType: type })

  // Area-prefix lookup
  const areaId = useAncestorTagStore((s) => s.projectToAreaMap[containerId])
  const areaName = useAncestorTagStore((s) =>
    areaId ? s.containerTitles[areaId] : undefined,
  )
  const containerIcon = useAncestorTagStore(
    (s) => s.containerIcons[containerId],
  )
  const percentage =
    progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0

  return (
    <div className="container-group-header">
      <div className="flex items-center gap-2 px-3 py-[5px] pb-2.5">
        {/* Icon: cube for area, pie for project, bars for list — primary color (v1 pattern) */}
        {isArea ? (
          <StaticIcon
            type="area"
            size={20}
            mode="monochrome"
            className="shrink-0"
          />
        ) : (
          <DynamicIcon
            icon={containerIcon}
            type={isProject ? "project" : "list"}
            percentage={percentage}
            unfinished={progress.unfinished}
            status={progress.status}
            size="small"
            color="var(--primary)"
            className="shrink-0"
          />
        )}

        {/* Area prefix + clickable title */}
        <div className="flex min-w-0 flex-1 items-center gap-0 truncate">
          {areaName && (
            <>
              <Link
                to={`/area/${areaId}`}
                className="shrink-0 text-[15px] font-medium text-foreground opacity-50 hover:opacity-70"
              >
                {areaName}
              </Link>
              <ChevronBigRight className="mx-0.5 h-4 w-4 shrink-0 text-foreground opacity-50" />
            </>
          )}
          <Link
            to={href}
            className="min-w-0 truncate text-[15px] font-medium text-foreground"
            style={{ opacity: hasTitle ? 1 : 0.5 }}
          >
            {displayTitle}
          </Link>
        </div>

      </div>
      <div className="mx-3 border-b border-foreground/16" />
    </div>
  )
})
